import { useEffect, useMemo, useState } from "react";
import { Button, Skeleton, Tab, Tabs } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import Card from "../../../components/ui/Card";
import PersonList from "../../management/components/PersonList";
import AddPersonModal from "../../management/components/AddPersonModal";
import EditUserGoalsDrawer from "./EditUserGoalsDrawer";
import BulkGoalsDrawer from "../bulkGoals/BulkGoalsDrawer";
import QrTemplateList from "../../management/components/QrTemplateList";
import InlineGroupNameEdit from "../../management/components/InlineGroupNameEdit";
import DeleteGroupConfirm from "../../management/components/DeleteGroupConfirm";
import RemovePersonConfirm from "../../management/components/RemovePersonConfirm";
import ProgressRangeBar, { matchingPreset } from "./ProgressRangeBar";
import ProgressSortButton from "./ProgressSortButton";
import GroupReportDrawer from "./GroupReportDrawer";
import ProgressSummary from "./ProgressSummary";
import MemberProgressDetail from "./MemberProgressDetail";
import MemberProgressList from "./MemberProgressList";
import GroupMuzakerePanel from "./GroupMuzakerePanel";
import GroupSettingsPanel from "./GroupSettingsPanel";
import GroupHighlightsView from "./insights/GroupHighlightsView";
import GroupTitlesView from "./insights/GroupTitlesView";
import GroupGeneralInfo from "./insights/GroupGeneralInfo";
import { soft } from "./insights/softClasses";
import {
  compareLabel,
  groupCompare,
  hasServerCompare,
  memberRate,
  previousPeriodRange,
} from "../../../lib/progressCompare";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../components/ui/Toast";
import {
  useDeleteGroupMutation,
  useGetGroupStatsQuery,
  useGetManagedGroupQuery,
  useRemoveGroupMemberMutation,
} from "../../../api/managementApi";
import { localDateString, parseLocalDate, startOfMonth } from "../../../lib/dateUtils";

// Sekme indeksleri (mobildeki grup sayfasıyla aynı sıra). İstatistik sorgusu
// yalnızca kendi sekmesinde atılıyor; üye yönetimi de orada yapıldığı için
// ilk sekme o. Enler, Ünvanlar ve Genel Bilgi kendi içinde kayar.
const TAB_STATS = 0;
const TAB_HIGHLIGHTS = 1;
const TAB_TITLES = 2;
const TAB_MUZAKERE = 3;
const TAB_MANAGERS = 4;
const TAB_QR = 5;
const TAB_GENERAL = 6;
const TAB_SETTINGS = 7;
const EDGE_TABS = [TAB_STATS, TAB_HIGHLIGHTS, TAB_TITLES, TAB_GENERAL, TAB_SETTINGS];

function defaultRange() {
  const now = new Date();
  return { start: localDateString(startOfMonth(now)), end: localDateString(now) };
}

function SectionTitle({ title, action }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="text-lg font-SemiBold text-terakkiInk dark:text-white">{title}</h3>
      {action}
    </div>
  );
}

/**
 * Sağ panel: seçili grubun tüm yönetim işleri tek yerde — üye/yönetici
 * yönetimi, üye hedeflerini düzenleme, ilerleme istatistikleri, QR şablonları
 * ve grup silme. Sayfa yalnızca yönetici olunan grupları listelediği için
 * burada ayrıca yetki kontrolü yapılmıyor.
 */
export default function GroupDetailPanel({ group, onDeleted }) {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const toast = useToast();
  const currentUserId = user?._id;

  const [tab, setTab] = useState(0);
  const [addPersonMode, setAddPersonMode] = useState(null);
  // Üyeyi kopya olarak değil kimlikle tutuyoruz; hedef eklenip güncellendiğinde
  // taze veriden türetilsin ve drawer içeriği anında değişsin.
  const [goalsMemberId, setGoalsMemberId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [range, setRange] = useState(defaultRange);
  const [statMemberId, setStatMemberId] = useState(null);
  const [statSortKey, setStatSortKey] = useState("completionRate");
  const [reportOpen, setReportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const groupId = group ? String(group.id) : null;

  // Grup değişince sekme başa dönsün.
  useEffect(() => {
    setTab(0);
    setStatMemberId(null);
  }, [groupId]);

  const { data: detailData, isFetching: detailLoading } = useGetManagedGroupQuery(
    { groupId },
    { skip: !groupId }
  );
  const detail = detailData?.data?.group;

  const statsValid = Boolean(range.start && range.end && range.start <= range.end);
  const { data: statsData, isFetching: statsLoading } = useGetGroupStatsQuery(
    { groupId, startDate: range.start, endDate: range.end },
    { skip: !groupId || tab !== TAB_STATS || !statsValid }
  );
  const userStats = useMemo(() => statsData?.data?.userStats || [], [statsData]);

  // Önceki eşit döneme göre kıyas: hazır dönem seçiliyse onun kuralıyla
  // (süren hafta geçen haftanın aynı günleri), elle aralıkta hemen öncesi.
  const periodKey = matchingPreset(range)?.key ?? null;
  const prevRange = useMemo(() => {
    if (!statsValid) return null;
    const prev = previousPeriodRange(periodKey, parseLocalDate(range.start), parseLocalDate(range.end));
    return { start: localDateString(prev.start), end: localDateString(prev.end) };
  }, [periodKey, range.start, range.end, statsValid]);
  // Yeni sunucu farkı üyelerde veriyor (previousRate, delta); önceki dönem
  // sorgusu yalnız eski sunucuda atılır.
  const serverCompare = hasServerCompare(userStats);
  const { data: prevStatsData } = useGetGroupStatsQuery(
    { groupId, startDate: prevRange?.start, endDate: prevRange?.end },
    {
      skip:
        !groupId || tab !== TAB_STATS || !prevRange || !statsData || statsLoading || serverCompare,
    }
  );
  const prevList = serverCompare ? null : prevStatsData?.data?.userStats;
  const previousById = useMemo(() => {
    const map = new Map();
    (prevList || []).forEach((u) => map.set(String(u.actorId), memberRate(u)));
    return map;
  }, [prevList]);
  const { averageDelta } = groupCompare({ members: userStats, previousMembers: prevList });
  const compareText = t(compareLabel(periodKey));

  const people = useMemo(
    () => detail?.members || group?.members || [],
    [detail, group]
  );
  const goalsMember = useMemo(
    () => people.find((m) => String(m.id) === String(goalsMemberId)) || null,
    [people, goalsMemberId]
  );

  const [deleteGroup, { isLoading: deleting }] = useDeleteGroupMutation();
  const [removeMember, { isLoading: removingMember }] = useRemoveGroupMemberMutation();
  const [pendingRemoval, setPendingRemoval] = useState(null);

  if (!group) {
    return (
      <Card className="flex flex-col items-center justify-center text-center min-h-[320px] lg:h-full">
        <GroupsIcon sx={{ fontSize: 40 }} className="text-terakkiBorder dark:text-white/30" aria-hidden />
        <p className="text-base text-terakkiNeutral mt-3 dark:text-white/70">{t("groups_pick_hint")}</p>
      </Card>
    );
  }

  const handleRemoveMember = async () => {
    if (!pendingRemoval) return;
    const result = await removeMember({ groupId, email: pendingRemoval.email });
    if (result.error) {
      toast.error(result.error.message || t("manage_remove_error"));
      return;
    }
    toast.success(t("manage_member_removed"));
    setPendingRemoval(null);
    setStatMemberId(null);
  };

  const handleDelete = async () => {
    const result = await deleteGroup({ groupId });
    if (result.error) {
      toast.error(result.error.message || t("manage_delete_error"));
      return;
    }
    toast.success(t("manage_delete_success"));
    setDeleteOpen(false);
    onDeleted(groupId);
  };


  const tabs = [
    t("groups_tab_progress"),
    t("Enler"),
    t("Ünvanlar"),
    t("muzakere_title"),
    t("manage_section_managers"),
    t("QR Katılım"),
    t("Genel Bilgi"),
    t("groups_tab_settings"),
  ];

  return (
    <Card className="!p-0 overflow-hidden flex flex-col min-h-[320px] max-h-[calc(100vh-3rem)] lg:h-full lg:max-h-full lg:min-h-0">
      <div className="shrink-0 px-4 py-3 bg-terakkiBrandCloud border-b border-terakkiBrandMist dark:bg-white/[0.06] dark:border-white/[0.14]">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-Bold text-terakkiPrimaryDark leading-tight truncate dark:text-white">
              {group.groupName}
            </div>
            <div className="text-xs text-terakkiNeutral leading-tight mt-0.5 dark:text-white/70">
              {group.memberCount} {t("groups_member_count")} · {group.totalManagers}{" "}
              {t("groups_manager_count")}
            </div>
          </div>
          <InlineGroupNameEdit groupId={groupId} currentName={group.groupName} />
        </div>
      </div>

      <div className="shrink-0 border-b border-terakkiBorder px-2 dark:border-white/[0.14]">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {tabs.map((label) => (
            <Tab key={label} label={label} sx={{ minHeight: 48, textTransform: "none", fontSize: 15 }} />
          ))}
        </Tabs>
      </div>

      <div
        className={`flex-1 min-h-0 ${
          tab === TAB_STATS ? "p-4 overflow-hidden" : EDGE_TABS.includes(tab) ? "overflow-hidden" : "p-4 overflow-y-auto"
        }`}
      >
        <ManagerTabs
          tab={tab}
          t={t}
          group={group}
          groupId={groupId}
          detail={detail}
          detailLoading={detailLoading}
          currentUserId={currentUserId}
          range={range}
          setRange={setRange}
          statsLoading={statsLoading}
          userStats={userStats}
          statMemberId={statMemberId}
          onSelectStatMember={setStatMemberId}
          statSortKey={statSortKey}
          onChangeStatSort={setStatSortKey}
          previousById={previousById}
          averageDelta={averageDelta}
          compareText={compareText}
          onOpenReport={() => setReportOpen(true)}
          onRemoveMember={setPendingRemoval}
          onAddPerson={setAddPersonMode}
          onBulkGoals={() => setBulkOpen(true)}
          canBulkGoals={people.length > 0}
          onEditGoals={(person) => setGoalsMemberId(person?.id ?? null)}
          onDelete={() => setDeleteOpen(true)}
        />
      </div>

      <AddPersonModal
        open={Boolean(addPersonMode)}
        mode={addPersonMode || "member"}
        groupId={groupId}
        onClose={() => setAddPersonMode(null)}
        onAdded={(mode) =>
          toast.success(mode === "manager" ? t("manage_manager_added") : t("manage_member_added"))
        }
      />
      <EditUserGoalsDrawer
        open={Boolean(goalsMemberId)}
        groupId={groupId}
        member={goalsMember}
        refreshing={detailLoading}
        onClose={() => setGoalsMemberId(null)}
      />
      <BulkGoalsDrawer
        open={bulkOpen}
        groupId={groupId}
        members={people}
        onClose={() => setBulkOpen(false)}
      />
      <GroupReportDrawer
        open={reportOpen}
        group={group}
        userStats={userStats}
        range={range}
        onClose={() => setReportOpen(false)}
      />
      <RemovePersonConfirm
        open={Boolean(pendingRemoval)}
        loading={removingMember}
        onClose={() => setPendingRemoval(null)}
        onConfirm={handleRemoveMember}
      />
      <DeleteGroupConfirm
        open={deleteOpen}
        groupName={group.groupName}
        loading={deleting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </Card>
  );
}

function ManagerTabs({
  tab,
  t,
  group,
  groupId,
  detail,
  detailLoading,
  currentUserId,
  range,
  setRange,
  statsLoading,
  userStats,
  statMemberId,
  onSelectStatMember,
  statSortKey,
  onChangeStatSort,
  previousById,
  averageDelta,
  compareText,
  onOpenReport,
  onRemoveMember,
  onAddPerson,
  onBulkGoals,
  canBulkGoals,
  onEditGoals,
  onDelete,
}) {
  if (detailLoading && !detail) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={56} />
      </div>
    );
  }

  if (tab === TAB_HIGHLIGHTS) {
    return <GroupHighlightsView groupId={groupId} />;
  }

  if (tab === TAB_TITLES) {
    return <GroupTitlesView groupId={groupId} />;
  }

  if (tab === TAB_MUZAKERE) {
    return <GroupMuzakerePanel groupId={groupId} />;
  }

  if (tab === TAB_SETTINGS) {
    return <GroupSettingsPanel key={groupId} groupId={groupId} />;
  }

  if (tab === TAB_QR) {
    return (
      <section>
        <SectionTitle title={t("manage_section_templates")} />
        <QrTemplateList
          groupId={groupId}
          groupName={group.groupName}
          templates={detail?.qrTemplates}
        />
      </section>
    );
  }

  if (tab === TAB_MANAGERS) {
    return (
      <section>
        <SectionTitle
          title={t("manage_section_managers")}
          action={
            <Button
              variant="contained"
              startIcon={<ShieldOutlinedIcon />}
              onClick={() => onAddPerson("manager")}
            >
              {t("groups_add_manager_cta")}
            </Button>
          }
        />
        <PersonList
          groupId={groupId}
          role="manager"
          people={detail?.managers || group.managers}
          currentUserId={currentUserId}
          emptyText={t("manage_managers_empty")}
        />
      </section>
    );
  }

  const selectedStatMember =
    (userStats || []).find((u) => String(u.actorId) === String(statMemberId)) || null;

  if (tab === TAB_STATS) {
    return (
      <div className="flex flex-col gap-5 lg:h-full lg:min-h-0">
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <ProgressRangeBar value={range} onChange={setRange} />
            <ProgressSortButton value={statSortKey} onChange={onChangeStatSort} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="contained"
              startIcon={<PersonAddAltIcon />}
              onClick={() => onAddPerson("member")}
            >
              {t("groups_add_member_cta")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PlaylistAddCheckIcon />}
              disabled={!canBulkGoals}
              onClick={onBulkGoals}
            >
              {t("bulk_title")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssessmentOutlinedIcon />}
              disabled={userStats.length === 0}
              onClick={onOpenReport}
            >
              {t("report_cta")}
            </Button>
          </div>
        </div>

        {statsLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-5 items-start">
            <div className="flex flex-col gap-3">
              <Skeleton variant="rounded" height={76} />
              <Skeleton variant="rounded" height={76} />
              <Skeleton variant="rounded" height={76} />
            </div>
            <div className="flex flex-col gap-3">
              <Skeleton variant="rounded" height={112} />
              <Skeleton variant="rounded" height={112} />
              <Skeleton variant="rounded" height={112} />
            </div>
          </div>
        ) : userStats.length === 0 ? (
          <p className="text-sm text-terakkiNeutral dark:text-white/70">{t("manage_stats_empty")}</p>
        ) : (
          // Solda özet ve hedef kırılımı alt alta, ortada üye listesi, bir üye
          // seçilince liste daralıp sağda o üyenin hedef dökümü açılıyor.
          <div
            className={`grid grid-cols-1 gap-5 items-start lg:items-stretch lg:flex-1 lg:min-h-0 ${
              selectedStatMember
                ? "lg:grid-cols-[280px_300px_minmax(0,1fr)]"
                : "lg:grid-cols-[300px_minmax(0,1fr)]"
            }`}
          >
            <ProgressSummary
              userStats={userStats}
              averageDelta={averageDelta}
              compareText={compareText}
            />
            <MemberProgressList
              userStats={userStats}
              sortKey={statSortKey}
              selectedId={statMemberId}
              compact={Boolean(selectedStatMember)}
              previousById={previousById}
              compareText={compareText}
              onSelect={(id) => onSelectStatMember(id === statMemberId ? null : id)}
            />
            {selectedStatMember && (
              // groupId: yöneticinin "Hedef Gir" düğmesi (W2) bu rotada URL'den
              // grup okuyamıyor; panelin grubu buradan gider.
              <MemberProgressDetail
                groupId={groupId}
                member={selectedStatMember}
                onClose={() => onSelectStatMember(null)}
                onEditGoals={(m) => onEditGoals({ id: m.actorId })}
                onRemove={onRemoveMember}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Kalan tek sekme: Genel Bilgi (TAB_GENERAL); Grubu Sil altta sabit.
  if (tab !== TAB_GENERAL) return null;

  const creator = detail?.createdBy || group.createdBy;
  const founderName =
    creator && typeof creator === "object"
      ? `${creator.name || ""} ${creator.surname || ""}`.trim()
      : "";

  return (
    <GroupGeneralInfo
      group={{ ...group, ...(detail || {}) }}
      founderName={founderName}
      footer={
        <DangerZone
          t={t}
          group={group}
          currentUserId={currentUserId}
          memberCount={(detail?.members || group.members || []).length}
          onDelete={onDelete}
        />
      }
    />
  );
}

/**
 * Grup silme kuralları sunucuda üç koşula bağlı (group.service.js:117):
 * grubu oluşturan kişi olmak, yönetici olmak ve grupta hiç üye kalmamış olmak.
 * Butonu gizlemek yerine pasif bırakıp nedenini yazıyoruz.
 */
function DangerZone({ t, group, currentUserId, memberCount, onDelete }) {
  const isCreator =
    !group.createdBy || String(group.createdBy.id || group.createdBy) === String(currentUserId);
  const hasMembers = memberCount > 0;
  const blockedReason = !isCreator
    ? t("groups_delete_requires_creator")
    : hasMembers
    ? t("groups_delete_requires_empty").replace("{n}", memberCount)
    : null;

  return (
    <section className="flex flex-col gap-2">
      <p className={`text-xs ${soft.muted}`}>{blockedReason || t("groups_delete_hint")}</p>
      {/* Silme dolu kırmızı: iki temada da belli olsun (mobil ile aynı). */}
      <Button
        fullWidth
        disableElevation
        variant="contained"
        disabled={Boolean(blockedReason)}
        onClick={onDelete}
        sx={{
          borderRadius: 999,
          height: 44,
          backgroundColor: "#C0392B",
          color: "#fff",
          "&:hover": { backgroundColor: "#A93226" },
        }}
      >
        {t("manage_delete_group_cta")}
      </Button>
    </section>
  );
}
